/**
 * useCapacitorLifecycle — app lifecycle management for Capacitor (iOS/Android).
 *
 * Responsibilities:
 * 1. Initialize safe area insets on mount + watch for changes (rotation etc.)
 * 2. Re-read safe area when app returns to foreground
 * 3. Configure status bar appearance
 * 4. Handle app state change events (foreground/background)
 * 5. Parse deep link auth URLs (totodo:///auth?email=&otp=&otpId=)
 * 6. Track keyboard open/height state
 * 7. Set keyboard resize mode
 * 8. Configure NativeBridge with API URLs
 * 9. Sync dark/light theme to native (iOS keyboard bg, status bar)
 *
 * This is the v2 equivalent of v1's useAppLifecycle + AppUrlListener.
 * Mount this ONCE at the top level (AppLayout or App).
 */

import { useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { Capacitor, type PluginListenerHandle } from "@capacitor/core"
import { App, type URLOpenListenerEvent } from "@capacitor/app"
import { StatusBar, Style as StatusBarStyle } from "@capacitor/status-bar"
import { NativeBridge } from "@/shared/plugins/NativeBridge"
import {
  initSafeAreaInsets,
  watchSafeAreaChanges,
} from "@/shared/utils/platform/safeArea"
import { appLifecycleHub } from "@/shared/utils/platform/appLifecycleHub"
import { db } from "@/db/schema"
import { useKeyboardStore } from "@/features/auth/stores/keyboardStore"
import { enhancedSmoothScroll } from "@/shared/utils/ui/enhancedSmoothScroll"
import {
  API_BASE_URL,
  DEXIE_CLOUD_URL,
  NOTIFICATION_LOGGING_ENABLED,
} from "@/config/environment"

/**
 * Scroll the currently focused element into view after the keyboard finishes
 * showing. For ProseMirror/TipTap editors, finds the actual cursor DOM node
 * rather than the editor root element. For contentEditable elements (title,
 * checklist), uses the Selection API to find the cursor position.
 *
 * Measures bottom chrome (toolbar slot + action panel) dynamically to
 * avoid scrolling behind fixed UI.
 */
function scrollFocusedElementIntoView(): void {
  const el = document.activeElement as HTMLElement | null
  if (!el || el === document.body) return

  // Skip elements whose own feature already owns the scroll — this is a
  // generic fallback, and running it on top of a feature's scroll made three
  // mechanisms react to the same keyboard-open event:
  //   .ProseMirror            — EditorShell + ProseMirror caret-follow
  //   [data-checklist-id]     — scrollChecklistRowIntoView
  //   [aria-label=Task title] — TaskItem's scrollOpenIntoView
  // See rule 9 in dreambase/ui/scroll-into-view.md.
  if (
    el.closest(".ProseMirror") ||
    el.closest("[data-checklist-id]") ||
    el.closest('[aria-label="Task title"]')
  ) {
    return
  }

  // Find the scroll container — main scroll area or any overflow container
  const container =
    el.closest<HTMLElement>("main.overflow-y-auto") ??
    el.closest<HTMLElement>(".overflow-y-auto")
  if (!container) return

  // For contentEditable elements, try to find the cursor node for precision
  let target: HTMLElement = el
  if (el.isContentEditable) {
    try {
      const sel = window.getSelection()
      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0)
        const node = range.startContainer
        const resolved =
          (node.nodeType === Node.ELEMENT_NODE
            ? node
            : node.parentElement) as HTMLElement | null
        if (resolved && container.contains(resolved)) {
          target = resolved
        }
      }
    } catch {
      // fallback to the focused element
    }
  }

  // Measure bottom chrome: editor toolbar slot + action panel
  const toolbarSlot = document.getElementById("editor-toolbar-slot")
  const toolbarH = toolbarSlot?.offsetHeight ?? 0
  const actionPanel = toolbarSlot?.nextElementSibling as HTMLElement | null
  const panelH = actionPanel?.offsetHeight ?? 0
  // Add generous padding so the element isn't scrolled to the very edge
  const bottomInset = toolbarH + panelH + 40

  enhancedSmoothScroll(target, {
    container,
    block: "nearest",
    duration: 300,
    offset: -20,
    bottomInset,
  })
}

/** Track processed OTP IDs to prevent duplicate login attempts (replay prevention). */
const processedOtpIds = new Set<string>()

/** Prevent concurrent login attempts from deep links. */
let isDeepLinkLoginInFlight = false

export function useCapacitorLifecycle() {
  const listenersRef = useRef<PluginListenerHandle[]>([])
  const cleanupWatchRef = useRef<(() => void) | null>(null)
  const setupDoneRef = useRef(false)
  const navigate = useNavigate()
  const navigateRef = useRef(navigate)
  navigateRef.current = navigate

  useEffect(() => {
    // Always initialize safe area (no-op on web)
    initSafeAreaInsets()

    if (!Capacitor.isNativePlatform()) return

    if (setupDoneRef.current) return
    setupDoneRef.current = true

    // ------------------------------------------------------------------
    // Async setup
    // ------------------------------------------------------------------
    async function setup() {
      // 1. Configure status bar — match the current theme
      // StatusBarStyle.Dark = light/white text (for dark backgrounds)
      // StatusBarStyle.Light = dark text (for light backgrounds)
      try {
        const isDarkInitial =
          document.documentElement.classList.contains("dark")
        await StatusBar.setStyle({
          style: isDarkInitial ? StatusBarStyle.Dark : StatusBarStyle.Light,
        })
        if (Capacitor.getPlatform() === "android") {
          await StatusBar.setOverlaysWebView({ overlay: true })
        }
      } catch (e) {
        console.warn("[Lifecycle] StatusBar config failed:", e)
      }

      // 2. Configure NativeBridge with API URLs (required for native sync)
      // Native HTTP clients need an absolute URL. If API_BASE_URL is absolute,
      // derive the origin from it; otherwise use the native-specific env var or
      // the hardcoded production URL. On native, window.location.origin is
      // "capacitor://localhost" which is NOT a real HTTP endpoint.
      try {
        const nativeApiBaseUrl = (() => {
          try {
            const parsed = new URL(API_BASE_URL)
            return parsed.origin
          } catch {
            // API_BASE_URL is relative (e.g. "/api") — use the native-specific
            // fallback which is always an absolute production URL.
            return (
              import.meta.env.VITE_API_BASE_URL_NATIVE ??
              "https://api.totodo.io"
            )
          }
        })()
        await NativeBridge.configure({
          apiBaseUrl: nativeApiBaseUrl,
          dexieCloudDbUrl: DEXIE_CLOUD_URL,
          debugLoggingEnabled: NOTIFICATION_LOGGING_ENABLED,
        })
        console.log(
          "[Lifecycle] NativeBridge configured with apiBaseUrl:",
          nativeApiBaseUrl,
        )
      } catch (e) {
        console.warn("[Lifecycle] NativeBridge.configure failed:", e)
      }

      // 3. Sync theme to native (iOS keyboard bg, status bar)
      // Watch for dark class changes on <html> and sync to native
      if (Capacitor.getPlatform() === "ios") {
        try {
          const { ThemeSync } = await import("@/shared/plugins/ThemeSyncPlugin")
          const { Keyboard } = await import("@capacitor/keyboard")
          const { KeyboardStyle } = await import("@capacitor/keyboard")
          const { isStatusBannerActive } = await import("@/app/components/AppStatusBanner")

          const syncTheme = async () => {
            const isDark = document.documentElement.classList.contains("dark")
            try {
              await ThemeSync.setTheme({
                theme: isDark ? "dark" : "light",
              })
              await Keyboard.setStyle({
                style: isDark ? KeyboardStyle.Dark : KeyboardStyle.Light,
              })
              // Skip status bar sync when AppStatusBanner is controlling it
              if (!isStatusBannerActive()) {
                await StatusBar.setStyle({
                  style: isDark ? StatusBarStyle.Dark : StatusBarStyle.Light,
                })
              }
            } catch {
              // Plugin may not be available on older builds
            }
          }

          // Initial sync
          await syncTheme()

          // Watch for class changes on <html>
          const observer = new MutationObserver((mutations) => {
            for (const m of mutations) {
              if (m.type === "attributes" && m.attributeName === "class") {
                void syncTheme()
                break
              }
            }
          })
          observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ["class"],
          })

          // Store cleanup
          listenersRef.current.push({
            remove: () => observer.disconnect(),
          } as unknown as PluginListenerHandle)

          console.log("[Lifecycle] Theme sync initialized (iOS)")
        } catch (e) {
          console.warn("[Lifecycle] Theme sync setup failed:", e)
        }
      }

      // 4. Watch for safe area changes (rotation, split-screen)
      cleanupWatchRef.current = await watchSafeAreaChanges()

      // 5. Re-read safe area + re-sync status bar on foreground (via centralized hub)
      const removeForeground = appLifecycleHub.on("foreground", () => {
        console.log("[Lifecycle] foreground — re-reading safe area + syncing status bar")
        initSafeAreaInsets()
        // Re-sync status bar style in case iOS overrode it while backgrounded
        const isDark = document.documentElement.classList.contains("dark")
        StatusBar.setStyle({
          style: isDark ? StatusBarStyle.Dark : StatusBarStyle.Light,
        }).catch(() => {})
      })
      // Store cleanup fn as a pseudo PluginListenerHandle
      listenersRef.current.push({
        remove: removeForeground,
      } as PluginListenerHandle)

      // 6. Deep link handler — parses auth URLs from OAuth callbacks
      // Server redirects to totodo:///auth?email=X&otpId=Y&otp=Z on native
      //
      // Safety measures (ported from v1 AppUrlListener):
      //   - Duplicate OTP prevention via processedOtpIds Set
      //   - Concurrent login guard via isDeepLinkLoginInFlight
      //   - 15s timeout to prevent hanging on stale/broken OTPs
      //   - Origin-based bounce: if Universal Links intercepted a web user's
      //     redirect, the origin param tells us to open external browser instead
      const urlListener = await App.addListener(
        "appUrlOpen",
        (event: URLOpenListenerEvent) => {
          console.log("[Lifecycle] appUrlOpen:", event.url)

          try {
            const url = new URL(event.url)
            const email = url.searchParams.get("email")
            const otp = url.searchParams.get("otp")
            const otpId = url.searchParams.get("otpId")
            const origin = url.searchParams.get("origin")

            if (email && otp && otpId) {
              // Origin-based bounce back (v1 parity):
              // If the user was browsing the WEB app and Universal Links intercepted
              // the redirect, origin will be "totodo.io" or "totodo.app" — meaning
              // the user intended to stay in the browser. Bounce them back.
              // A dev web origin is "localhost:<port>" and nothing more: the URL below is built from
              // it, and "localhost:1@evil.com" would open evil.com (anyone can fire this deep link).
              const devWebOrigin = origin !== null && /^localhost:\d{1,5}$/.test(origin)
              if (origin && (origin.includes("totodo.io") || origin.includes("totodo.app") || devWebOrigin)) {
                console.log(
                  `[Lifecycle] Origin=${origin} indicates web user — bouncing to browser`,
                )
                const params = new URLSearchParams({ email, otp, otpId })
                // Open in external browser so user stays in web context
                import("@capacitor/browser")
                  .then(({ Browser }) => {
                    // Use the origin to determine which domain to open
                    const webDomain = origin.includes("totodo.io")
                      ? "https://totodo.io"
                      : origin.includes("totodo.app")
                        ? "https://totodo.app"
                        : `http://${origin}`
                    void Browser.open({ url: `${webDomain}/?${params.toString()}` })
                  })
                  .catch(() => {
                    // Fallback: log in locally if browser open fails
                    console.warn("[Lifecycle] Browser.open failed, logging in locally")
                    void db.cloud.login({ email, otp, otpId })
                  })
                return
              }

              // Duplicate OTP prevention — same OTP should never be processed twice
              if (processedOtpIds.has(otpId)) {
                console.log(
                  `[Lifecycle] OTP ID ${otpId} already processed, skipping`,
                )
                return
              }

              // Concurrent login guard
              if (isDeepLinkLoginInFlight) {
                console.log(
                  "[Lifecycle] Login already in progress, skipping",
                )
                return
              }

              console.log("[Lifecycle] Auth deep link detected — logging in")
              isDeepLinkLoginInFlight = true
              processedOtpIds.add(otpId)
              const loginStart = performance.now()

              // Race login against a 15s timeout (v1 parity)
              const loginPromise = db.cloud.login({ email, otp, otpId })
              const timeoutPromise = new Promise<never>((_, reject) => {
                setTimeout(
                  () => reject(new Error("Login timeout after 15 seconds")),
                  15_000,
                )
              })

              void Promise.race([loginPromise, timeoutPromise])
                .then(() => {
                  const duration = performance.now() - loginStart
                  console.log(
                    `[Lifecycle] Deep link login succeeded in ${Math.round(duration)}ms`,
                  )

                  // Close the in-app browser if it's still open
                  import("@capacitor/browser")
                    .then(({ Browser }) => Browser.close())
                    .catch(() => {
                      // Browser plugin may not be available
                    })
                })
                .catch((err) => {
                  console.error("[Lifecycle] Deep link login failed:", err)
                  // Remove from processed set so user can retry
                  processedOtpIds.delete(otpId)
                })
                .finally(() => {
                  isDeepLinkLoginInFlight = false
                })
              return
            }

            // Non-auth deep link — navigate to the target route.
            // Handles: https://totodo.app/today?tid=abc, totodo:///task/abc,
            // totodo:///project/x?tid=y, etc.
            let pathname = url.pathname
            if (!pathname.startsWith("/")) pathname = "/" + pathname
            // Normalize /auth without credentials to root
            if (pathname === "/auth") pathname = "/"
            const slug = pathname + url.search
            console.log("[Lifecycle] Navigation deep link:", slug)
            navigateRef.current(slug)
          } catch {
            // Not a valid URL — ignore
          }
        },
      )
      listenersRef.current.push(urlListener)

      // 7. Keyboard tracking (native only)
      if (Capacitor.isPluginAvailable("Keyboard")) {
        try {
          const { Keyboard } = await import("@capacitor/keyboard")
          const { setKeyboard, clearKeyboard } = useKeyboardStore.getState()

          const kbWillShow = await Keyboard.addListener(
            "keyboardWillShow",
            (info) => {
              setKeyboard({ isOpen: false, height: info.keyboardHeight })
            },
          )
          listenersRef.current.push(kbWillShow)

          const kbDidShow = await Keyboard.addListener(
            "keyboardDidShow",
            (info) => {
              setKeyboard({ isOpen: true, height: info.keyboardHeight })
              // Auto-scroll the focused element into view after keyboard
              // animation completes and WebView resize has propagated.
              // 500ms: primary attempt after layout settle.
              // 1000ms: safety net — matches EditorShell's proven timing.
              setTimeout(scrollFocusedElementIntoView, 500)
              setTimeout(scrollFocusedElementIntoView, 1000)
            },
          )
          listenersRef.current.push(kbDidShow)

          const kbWillHide = await Keyboard.addListener(
            "keyboardWillHide",
            () => {
              clearKeyboard()
            },
          )
          listenersRef.current.push(kbWillHide)

          console.log("[Lifecycle] Keyboard listeners registered")
        } catch (e) {
          console.warn("[Lifecycle] Keyboard plugin setup failed:", e)
        }
      }

      // 8. Status bar tap → scroll to top (iOS native behaviour)
      // Handled by useScrollToTop hook in MobileLayout — provides smart
      // context-aware scrolling (sidebar, settings, active task, main).
      // The native statusTap listener is registered there.

      console.log("[Lifecycle] Capacitor setup complete")
    }

    setup().catch((err) => console.error("[Lifecycle] Setup failed:", err))

    // ------------------------------------------------------------------
    // Cleanup
    // ------------------------------------------------------------------
    return () => {
      setupDoneRef.current = false

      // Remove plugin listeners
      for (const handle of listenersRef.current) {
        handle.remove()
      }
      listenersRef.current = []

      // Remove safe area watcher
      cleanupWatchRef.current?.()
      cleanupWatchRef.current = null
    }
  }, [])
}
